/* eslint-disable react-hooks/exhaustive-deps */
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentShell from "components/ContentShell";
import { InputShell } from "components/FormInputShell";
import { SimpleTextInput } from "components/FormTextInput";
import { SimpleTimeDateInput } from "components/FormTimeDateInput";
import OperatorMultiSelect from "components/OperatorMultiSelect";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getChallengeRewardImages } from "services/challengeRewardImageService";
import {
  createDailyRewardCycle,
  getDailyRewardCycleInfo,
  getDailyRewardCycles,
  updateDailyRewardCycle,
  uploadSlotImage,
} from "services/dailyRewardCycleService";
import { getBoosters, getPlayerPacks } from "services/packServices";
import sessionHelper from "services/sessionHelper";
import debounce from "lodash.debounce";
import useStyles from "styles/banner/bannerEditor";
import {
  currentWeekStart,
  formatUtcDay,
  formatWeekRange,
  localDayToUtcInstant,
  nextFreeWeekStart,
  toLocalMonday,
  utcInstantToLocalDay,
  weekEnd,
  weekKey,
} from "utils/dailyRewardWeek";
import { getCyclePhase } from "utils/tableHelper/dailyRewardCyclesTableHelper";
import { dailyRewardCycleSchema } from "utils/validation";
import {
  isRewardComplete,
  resolveRewardImageType,
  RewardOption,
} from "./RewardFields";
import SlotCard from "./SlotCard";

const defaultPageNo = 1;
const defaultPageSize = 10;
const REWARD_PACK_FILTER = { type: "Reward", isDisabled: false };

const EMPTY_REWARD = () => ({
  awardType: "COINS",
  rewardHC: null,
  xpAmount: null,
  pack: null,
  booster: null,
  count: null,
  imagePath: null,
  imageUrl: null,
  rewardImageType: null,
});

const EMPTY_SLOT = (slotNo: number) => ({
  slotNo,
  ...EMPTY_REWARD(),
  operatorRewards: [],
});

const INITIAL_CYCLE = (isDefault: boolean) => ({
  name: "",
  isDefault,
  startDate: null,
  operators: [],
  excludedOperators: [],
  isActive: true,
  slots: [1, 2, 3, 4, 5, 6, 7].map(EMPTY_SLOT),
});

/**
 * Mirrors the manager's DUPLICATE_OPERATOR_IN_SLOT and INVALID_SLOT_CONFIG rules so the admin gets a
 * per-day message instead of a generic server error. The server still enforces them.
 */
const validateOperatorOverrides = (payload: any) => {
  for (const slot of payload.slots) {
    const seen = new Set<string>();

    for (const override of slot.operatorRewards ?? []) {
      if (!override.operator) {
        throw new Error(
          `Day ${slot.slotNo}: select an operator for every operator level reward`,
        );
      }
      if (seen.has(override.operator)) {
        throw new Error(
          `Day ${slot.slotNo}: the same operator is configured twice`,
        );
      }
      seen.add(override.operator);

      const filled =
        (override.awardType === "COINS" && override.rewardHC > 0) ||
        (override.awardType === "XP" && override.xpAmount > 0) ||
        (override.awardType === "PACK" && !!override.pack) ||
        (override.awardType === "BOOSTER" && !!override.booster);

      if (!filled) {
        throw new Error(
          `Day ${slot.slotNo}: complete the reward for the selected operator`,
        );
      }
    }
  }
};

// imageUrl and the __-prefixed keys are CMS-side editing state, not part of the stored cycle.
// celebrate runs with no allowUnknown, so leaving any of them on the payload would be a hard 400.
// The image type is re-derived on the way out so what is stored is always what the editor showed:
// only coins keep a chosen value, and a reward switched to a pack or booster cannot carry a stale
// image type it no longer displays.
const stripEditorFields = ({
  imageUrl,
  __file,
  __dims,
  __previewUrl,
  ...reward
}: any) => ({ ...reward, rewardImageType: resolveRewardImageType(reward) });

const stripPreviewFields = (payload: any) => ({
  ...payload,
  slots: payload.slots.map(({ operatorRewards, ...slot }: any) => ({
    ...stripEditorFields(slot),
    operatorRewards: (operatorRewards ?? []).map(stripEditorFields),
  })),
});

/**
 * A date the admin cannot type: the derived week end, and the start of a week that is already
 * running or over. Rendered from UTC parts so it always names the day the server stored, whatever
 * timezone the browser is in.
 *
 * It is the same outlined TextField at the same 38px height that SimpleTextInput renders, rather
 * than a bordered Typography: side by side in one row, a hand-rolled box never matches MUI's border
 * colour, radius and height.
 */
const StaticDateField = ({
  label,
  value,
}: {
  label: string;
  value?: string | Date | null;
}) => (
  <InputShell label={label} name={label} removeBottomMargin>
    <TextField
      value={formatUtcDay(value)}
      variant="outlined"
      fullWidth
      inputProps={{ readOnly: true }}
      InputProps={{ style: { height: 38 } }}
    />
  </InputShell>
);

const SectionCard = ({
  step,
  icon,
  title,
  subtitle,
  accent = "#1976d2",
  children,
  isComplete,
}: {
  step: number;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  accent?: string;
  children: React.ReactNode;
  isComplete?: boolean;
}) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: isComplete ? `${accent}60` : "divider",
      borderRadius: 3,
      mb: 3,
      overflow: "hidden",
      transition: "border-color 0.3s, box-shadow 0.3s",
      "&:hover": { boxShadow: `0 2px 16px ${accent}12` },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderBottom: "1px solid",
        borderBottomColor: "divider",
        borderLeft: `4px solid ${accent}`,
        background: `linear-gradient(135deg, ${accent}0a 0%, transparent 100%)`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
          boxShadow: `0 2px 8px ${accent}40`,
        }}
      >
        {step}
      </Box>
      <Box sx={{ color: accent, display: "flex", fontSize: 20 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="text.primary"
          sx={{ lineHeight: 1.3 }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {isComplete !== undefined && (
        <Tooltip
          title={isComplete ? "Section complete" : "Fill in required fields"}
        >
          <Box
            sx={{ display: "flex", color: isComplete ? "#4caf50" : "#bdbdbd" }}
          >
            {isComplete ? (
              <CheckCircleIcon fontSize="small" />
            ) : (
              <RadioButtonUncheckedIcon fontSize="small" />
            )}
          </Box>
        </Tooltip>
      )}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

const DailyRewardCycleEditor = () => {
  const styles = useStyles();
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [searchParams] = useSearchParams();

  const isCreate = !id;
  // The list decides which kind is being created — Add Default before one exists, Add Week after —
  // and the server rejects the other case anyway, so there is no kind picker inside the editor.
  const createsDefault = isCreate && searchParams.get("type") !== "week";

  const [loading, setLoading] = useState(false);
  const [cycle, setCycle] = useState<any>(() => INITIAL_CYCLE(createsDefault));
  const [takenWeeks, setTakenWeeks] = useState<string[]>([]);
  const [packOptions, setPackOptions] = useState<RewardOption[]>([]);
  const [boosterOptions, setBoosterOptions] = useState<RewardOption[]>([]);
  const [rewardImageOptions, setRewardImageOptions] = useState<RewardOption[]>(
    [],
  );
  // A fresh cycle has nothing configured, so every day opens; an existing one starts collapsed and
  // is read from the per-day summaries in each header.
  const [expandedSlots, setExpandedSlots] = useState<Record<number, boolean>>(
    () => Object.fromEntries([1, 2, 3, 4, 5, 6, 7].map((n) => [n, !id])),
  );

  const cancel = () => navigate(configData.routes.dailyRewardCycles.list);

  const operatorOptions = useMemo(() => {
    const all: RewardOption[] = sessionHelper.operatorOptions ?? [];
    if (cycle.isDefault) return all;

    const excluded = new Set(cycle.excludedOperators ?? []);
    if (cycle.operators?.length) {
      const included = new Set(cycle.operators);
      return all.filter((op) => included.has(op.id));
    }

    return all.filter((op) => !excluded.has(op.id));
  }, [cycle.isDefault, cycle.operators, cycle.excludedOperators]);

  const loadPacks = async (search?: string) => {
    try {
      const resp = await getPlayerPacks(
        search
          ? { search, skipPagination: true, ...REWARD_PACK_FILTER }
          : {
              pageNo: defaultPageNo,
              pageSize: defaultPageSize,
              ...REWARD_PACK_FILTER,
            },
      );

      if (resp?.items) {
        setPackOptions(
          resp.items.map((p: any) => ({
            id: p._id,
            label: p.name,
            imageUrl: p.imageUrl ?? null,
          })),
        );
      }
    } catch (error: any) {
      showSnackbar(error.message, "error");
    }
  };

  const onPackSearch = useCallback(
    debounce((value: string) => loadPacks(value), 300),
    [],
  );

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [, boostersResp, imagesResp, cyclesResp] = await Promise.all([
        loadPacks(),
        getBoosters(),
        getChallengeRewardImages(),
        // Only the availability facts matter here, so ask for the smallest page there is.
        getDailyRewardCycles({ pageSize: 1 }),
      ]);

      setBoosterOptions(
        (boostersResp?.data ?? []).map((b: any) => ({
          id: b._id,
          label: b.displayName,
          imageUrl: b.imageUrl ?? null,
        })),
      );

      // Reward-image S3 keys are deterministic, so replacing a default reuses the same URL — bust
      // the cache or the preview keeps showing the image that was replaced.
      const stamp = Date.now();

      // These supply the artwork behind each image type; which types a reward may use is decided in
      // RewardFields, not here.
      setRewardImageOptions(
        (imagesResp?.data ?? []).map((img: any) => ({
          id: img.rewardType,
          label: img.rewardType,
          imageUrl: img.imageUrl
            ? `${img.imageUrl.split("?")[0]}?t=${stamp}`
            : null,
        })),
      );

      // Every current-or-future week that already has a cycle, reported by the server for the whole
      // collection rather than one page — a week taken three pages down is still taken.
      const allTakenWeeks: string[] = (cyclesResp?.data?.takenWeeks ?? []).map(
        (week: string) => weekKey(week),
      );

      if (isCreate) {
        setTakenWeeks(allTakenWeeks);

        if (!createsDefault) {
          setCycle((c: any) => ({
            ...c,
            startDate: nextFreeWeekStart(allTakenWeeks).toISOString(),
          }));
        }
      } else {
        const resp = await getDailyRewardCycleInfo(id as string);
        const fetched = resp?.data;

        // The cycle being edited keeps its own week available, otherwise saving it unchanged would
        // read as a clash with itself.
        const ownWeek = fetched?.startDate ? weekKey(fetched.startDate) : null;
        setTakenWeeks(allTakenWeeks.filter((week) => week !== ownWeek));

        if (fetched) {
          setCycle({
            ...fetched,
            operators: (fetched.operators ?? []).map(String),
            excludedOperators: (fetched.excludedOperators ?? []).map(String),
            slots: [1, 2, 3, 4, 5, 6, 7].map((slotNo) => {
              const stored = (fetched.slots ?? []).find(
                (s: any) => s.slotNo === slotNo,
              );

              return stored
                ? {
                    ...EMPTY_SLOT(slotNo),
                    ...stored,
                    pack: stored.pack ? String(stored.pack) : null,
                    booster: stored.booster ? String(stored.booster) : null,
                    operatorRewards: (stored.operatorRewards ?? []).map(
                      (o: any) => ({
                        ...EMPTY_REWARD(),
                        ...o,
                        operator: String(o.operator),
                        pack: o.pack ? String(o.pack) : null,
                        booster: o.booster ? String(o.booster) : null,
                      }),
                    ),
                  }
                : EMPTY_SLOT(slotNo);
            }),
          });
        }
      }
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const toggleSlot = (slotNo: number) =>
    setExpandedSlots((prev) => ({ ...prev, [slotNo]: !prev[slotNo] }));

  const setAllSlots = (open: boolean) =>
    setExpandedSlots(
      Object.fromEntries([1, 2, 3, 4, 5, 6, 7].map((n) => [n, open])),
    );

  /**
   * Uploads every image the admin picked but has not saved yet, replacing the local File with the
   * stored key. Uploading here rather than on selection is what makes the Image Dimensions control
   * meaningful — the admin can still change the target size after choosing the file.
   */
  const uploadPendingImages = async (payload: any) => {
    const upload = async (reward: any) => {
      if (!reward.__file) return reward;

      const uploaded = await uploadSlotImage(reward.__file, reward.__dims);

      // The stored URL replaces the local preview, so the blob it pinned can go.
      if (reward.__previewUrl) URL.revokeObjectURL(reward.__previewUrl);

      return {
        ...reward,
        imagePath: uploaded.imagePath,
        imageUrl: uploaded.imageUrl,
        __file: null,
        __dims: null,
        __previewUrl: null,
      };
    };

    const slots = [];
    for (const slot of payload.slots) {
      const uploadedSlot = await upload(slot);
      const operatorRewards = [];

      for (const override of slot.operatorRewards ?? []) {
        operatorRewards.push(await upload(override));
      }

      slots.push({ ...uploadedSlot, operatorRewards });
    }

    return { ...payload, slots };
  };

  const onSlotChange = (slotNo: number, patch: Record<string, any>) =>
    setCycle((c: any) => ({
      ...c,
      slots: c.slots.map((slot: any) =>
        slot.slotNo === slotNo ? { ...slot, ...patch } : slot,
      ),
    }));

  const onOverrideAdd = (slotNo: number) =>
    setCycle((c: any) => ({
      ...c,
      slots: c.slots.map((slot: any) =>
        slot.slotNo === slotNo
          ? {
              ...slot,
              operatorRewards: [
                ...slot.operatorRewards,
                { operator: "", ...EMPTY_REWARD() },
              ],
            }
          : slot,
      ),
    }));

  const onOverrideChange = (
    slotNo: number,
    index: number,
    patch: Record<string, any>,
  ) =>
    setCycle((c: any) => ({
      ...c,
      slots: c.slots.map((slot: any) =>
        slot.slotNo === slotNo
          ? {
              ...slot,
              operatorRewards: slot.operatorRewards.map((o: any, i: number) =>
                i === index ? { ...o, ...patch } : o,
              ),
            }
          : slot,
      ),
    }));

  const onOverrideRemove = (slotNo: number, index: number) =>
    setCycle((c: any) => ({
      ...c,
      slots: c.slots.map((slot: any) =>
        slot.slotNo === slotNo
          ? {
              ...slot,
              operatorRewards: slot.operatorRewards.filter(
                (_: any, i: number) => i !== index,
              ),
            }
          : slot,
      ),
    }));

  /**
   * Mirrors the manager's week rules so a bad week is named in plain English before the round trip.
   * The server enforces all three regardless — this only decides which message the admin reads.
   */
  const validateWeek = (payload: any) => {
    if (payload.isDefault) return;

    if (!payload.startDate) {
      throw new Error("Pick the week this cycle covers");
    }

    const week = weekKey(payload.startDate);

    if (week < weekKey(new Date())) {
      throw new Error(
        "A cycle can only start on the current week or a later one",
      );
    }

    if (takenWeeks.includes(week)) {
      throw new Error(
        `${formatWeekRange(payload.startDate)} already has a cycle. Pick a different week.`,
      );
    }
  };

  const submit = async () => {
    try {
      setLoading(true);

      const payload: any = { ...cycle, ...(!isCreate && { _id: id }) };

      // Both trims sit AFTER validate on purpose: deleting a key first turns a payload decision
      // into a validation failure, which is how "isDisabled is a required field" reached QA on
      // every scheduled pack.
      await dailyRewardCycleSchema.validate(payload, { abortEarly: false });
      validateOperatorOverrides(payload);
      validateWeek(payload);

      const uploaded = await uploadPendingImages(payload);
      setCycle(uploaded);

      const body = stripPreviewFields(uploaded);

      if (body.isDefault) {
        delete body.startDate;
        delete body.operators;
        delete body.excludedOperators;
      }

      delete body.endDate;

      const response = isCreate
        ? await createDailyRewardCycle(body)
        : await updateDailyRewardCycle(body);

      showSnackbar(response.message, "success");
      cancel();
    } catch (error: any) {
      // abortEarly: false puts every failure on err.errors; err.message alone hides the rest.
      // The else branch catches the plain Error thrown by validateOperatorOverrides.
      if (error?.errors?.length) {
        error.errors.forEach((message: string) =>
          showSnackbar(message, "error"),
        );
      } else {
        showSnackbar(error.message, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // The phase is the only thing deciding how much of the editor is writable, and it mirrors the
  // manager's own rules: an ended week is history and cannot be rewritten; a running week keeps its
  // start date, since moving it would pull the live cycle out from under every player mid-week, but
  // its rewards stay editable so a mid-week correction is still possible.
  const phase = getCyclePhase(cycle);
  const isReadOnly = phase === "past";
  const isWeekLocked = phase === "live";

  const rewardProps = {
    packOptions,
    boosterOptions,
    rewardImageOptions,
    onPackSearch,
  };

  const slotsComplete = cycle.slots.every(
    (slot: any) =>
      isRewardComplete(slot) &&
      (slot.operatorRewards ?? []).every(
        (o: any) => !!o.operator && isRewardComplete(o),
      ),
  );

  const overrideCount = cycle.slots.reduce(
    (total: number, slot: any) => total + (slot.operatorRewards?.length ?? 0),
    0,
  );

  // Declared once and placed differently per cycle kind: full width on the default, sharing a row
  // with the week on a weekly one.
  const nameField = (
    <SimpleTextInput
      label="Name"
      name="name"
      value={cycle.name}
      onChange={(e: any) =>
        setCycle((c: any) => ({ ...c, name: e.target.value }))
      }
    />
  );

  const editorTitle = cycle.isDefault
    ? `${isCreate ? "Create" : "Edit"} Default Cycle`
    : `${isCreate ? "Add" : isReadOnly ? "View" : "Edit"} Week${
        cycle.startDate ? ` — ${formatWeekRange(cycle.startDate)}` : ""
      }`;

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={editorTitle}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button variant="outlined" color="inherit" onClick={cancel}>
            {isReadOnly ? "Back" : "Cancel"}
          </Button>
          {!isReadOnly && (
            <Button variant="contained" onClick={submit}>
              Save
            </Button>
          )}
        </Box>
      }
    >
      <Box sx={{ maxWidth: 960, pb: 4 }}>
        <SectionCard
          step={1}
          icon={<CalendarMonthIcon />}
          title="Cycle details"
          subtitle={
            cycle.isDefault
              ? "The fallback every week without a cycle of its own uses"
              : "The week this cycle covers and the operators it targets"
          }
          accent="#7b1fa2"
          isComplete={!!cycle.name}
        >
          <Alert severity="info" icon={false} sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              <b>Seven slots, Monday to Sunday.</b> Opening the game earns that
              day's stamp; the home screen claims every pending stamp. Resets{" "}
              <b>Monday 00:00 UTC</b> — unclaimed stamps are lost. Claimed days
              stay claimed, whatever you change here.
            </Typography>
            {cycle.isDefault && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <b>Default cycle</b> — used by every week that has none of its
                own. No dates, and it cannot be deleted or deactivated.
              </Typography>
            )}
          </Alert>

          {isReadOnly && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="body2">
                <b>This week has ended.</b> Read-only — its rewards were granted
                as configured at the time.
              </Typography>
            </Alert>
          )}

          {isWeekLocked && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="body2">
                <b>Running right now.</b> Rewards can still be corrected and
                reach players on their next refresh, but the week can no longer
                be moved. Claimed days stay claimed.
              </Typography>
            </Alert>
          )}

          <Box
            sx={{
              ...(isReadOnly && { pointerEvents: "none", opacity: 0.75 }),
            }}
          >
            {/* The default cycle has no week, so its name takes the whole card rather than sitting
                next to two empty columns. */}
            {cycle.isDefault && <Box sx={{ mb: 2 }}>{nameField}</Box>}

            {/* Name and the week it covers read as one line on a weekly cycle; who it reaches is the
                separate concern on the row below. Neither renders for the default cycle. */}
            {!cycle.isDefault && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                {/* Same basis on all three, so they grow into exact thirds of the row. */}
                <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>{nameField}</Box>

                <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                  {/* Only Mondays are selectable, weeks already covered are greyed out, and the end
                      date is never typed — the server derives it from the start. Values cross the
                      picker boundary as local calendar days, so a stored Monday 00:00 UTC does not
                      render as the previous Sunday west of UTC or snap back a week east of it. */}
                  {isWeekLocked || isReadOnly ? (
                    <StaticDateField
                      label="Week starting (Monday)"
                      value={cycle.startDate}
                    />
                  ) : (
                    <SimpleTimeDateInput
                      label="Week starting (Monday)"
                      name="startDate"
                      value={
                        cycle.startDate
                          ? utcInstantToLocalDay(cycle.startDate)
                          : null
                      }
                      format="dd/MM/yyyy"
                      views={["year", "month", "day"]}
                      readOnly={false}
                      removeBottomMargin={true}
                      minDate={utcInstantToLocalDay(currentWeekStart())}
                      shouldDisableDate={(day: Date) =>
                        day.getDay() !== 1 ||
                        takenWeeks.includes(weekKey(localDayToUtcInstant(day)))
                      }
                      onChange={(value: any) => {
                        // Half-typed input arrives as an Invalid Date; keep the last good week
                        // rather than letting toISOString throw inside the handler.
                        if (!value || Number.isNaN(new Date(value).getTime())) {
                          return;
                        }

                        setCycle((c: any) => ({
                          ...c,
                          startDate: localDayToUtcInstant(
                            toLocalMonday(value),
                          ).toISOString(),
                        }));
                      }}
                    />
                  )}
                </Box>

                <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                  <StaticDateField
                    label="Week ending (Sunday)"
                    value={cycle.startDate ? weekEnd(cycle.startDate) : null}
                  />
                </Box>
              </Box>
            )}

            {/* size="small" keeps these two the same height as every other field in the card;
                left at the MUI default they stand a good deal taller than their neighbours. */}
            {!cycle.isDefault && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
                  <OperatorMultiSelect
                    label="Operators"
                    size="small"
                    value={cycle.operators}
                    onChange={(operators: any) =>
                      setCycle((c: any) => ({ ...c, operators }))
                    }
                  />
                </Box>
                <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
                  <OperatorMultiSelect
                    label="Excluded operators"
                    size="small"
                    value={cycle.excludedOperators}
                    onChange={(excludedOperators: any) =>
                      setCycle((c: any) => ({ ...c, excludedOperators }))
                    }
                  />
                </Box>
              </Box>
            )}
          </Box>
        </SectionCard>

        <SectionCard
          step={2}
          icon={<ViewWeekIcon />}
          title="The seven days"
          subtitle="Monday through Sunday. Each day has one base reward, plus any number of per-operator overrides."
          accent="#00897b"
          isComplete={slotsComplete}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 2,
            }}
          >
            <Chip
              size="small"
              label="BASE REWARD"
              sx={{
                bgcolor: "#00897b",
                color: "white",
                fontWeight: 700,
                fontSize: 10,
                height: 20,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              what every operator gets
            </Typography>
            <Chip
              size="small"
              label="OPERATOR OVERRIDE"
              sx={{
                bgcolor: "#ef6c00",
                color: "white",
                fontWeight: 700,
                fontSize: 10,
                height: 20,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              replaces it for one operator, on that day only
              {overrideCount > 0 && ` · ${overrideCount} configured`}
            </Typography>

            <Box sx={{ flex: 1 }} />

            <Button size="small" onClick={() => setAllSlots(true)}>
              Expand all
            </Button>
            <Button size="small" onClick={() => setAllSlots(false)}>
              Collapse all
            </Button>
          </Box>

          {cycle.slots.map((slot: any) => (
            <SlotCard
              key={slot.slotNo}
              slot={slot}
              readOnly={isReadOnly}
              operatorOptions={operatorOptions}
              expanded={!!expandedSlots[slot.slotNo]}
              onToggle={toggleSlot}
              onSlotChange={onSlotChange}
              onOverrideAdd={onOverrideAdd}
              onOverrideChange={onOverrideChange}
              onOverrideRemove={onOverrideRemove}
              {...rewardProps}
            />
          ))}
        </SectionCard>
      </Box>
    </ContentShell>
  );
};

export default DailyRewardCycleEditor;
